import { getToken } from '@/api/upload'
import { apiGetCountryList } from '@/api/user'
import { apiGetClientConfigure } from '@/api/room'
import { apiGetContinentCountryList } from '@/api/home'
import type { countryInterface } from '@/types/apiType/userType'
import type { continentCountryInterface } from '@/types/apiType/homeType'
import type { clientConfigureInterface } from '@/types/apiType/roomType'
import type { ActionSheetAction } from 'vant'

export const usePublicStore = defineStore({
  id: 'public',
  state: () => ({
    isShowView: false, // 是否显示视图，用做首页国家
    freeCardPopupConfig: {
      // 是否显示获得免费券弹窗
      isShow: false,
      isLazyShow: false
    },
    rechargePopupConfig: {
      //  是否显示充值弹窗
      isShow: false,
      isLazyShow: false
    },
    qnToken: '', //七牛云上传token
    countryLanguage: '' as string, // 国家语言,用来做判断国家列表获取使用
    countryList: [] as countryInterface[], //国家列表
    continentCountryList: [] as continentCountryInterface[], // 国家大洲列表
    rechargeTipsPopupConfig: {
      isShow: false, // 是否显示充值提示
      isLazyShow: false,
      tipsText: useNuxtApp().$i18n.t('call.insufficientBalanceTips') as string, // 是否显示充值提示的默认提示语
      toType: 'page' as 'page' | 'popup' // 点击跳转类型 page:充值页面, popup:充值弹窗
    },
    webViewPopupConfig: {
      // webView配置
      iShow: false as boolean, // webView 开启关闭
      isLazyShow: false,
      src: '' as string, // 打开的链接
      popupClass: '' // 弹窗类名
    },
    downloadAppTipsPopupConfig: {
      isShow: false, // 引流至App提示
      isLazyShow: false,
      tipsText: useNuxtApp().$i18n.t('downloadAppTips.GetTips') as string, // 引流至App提示
      trackingLogType: 'msg' as 'msg' | 'call' | 'live' // 埋点类型: 消息页，通话后，直播列表页
    },
    isPageVisible: true as boolean, // 页面是否显示
    keepAliveIncludePage: ['Match', 'Home', 'Chat', 'Aboutme'] as string[], // keepaLive要缓存的页面
    isIOSWebView: false as boolean, // 是否在IOSWebView打开的页面
    clientConfigure: {
      gameConfigList: [] as clientConfigureInterface['gameConfigList'],
      detonatingTotal: [] as clientConfigureInterface['detonatingTotal']
    } as clientConfigureInterface, // 客户端配置
    actionSheetConfig: {
      isShow: false, // 全局ActionSheet配置
      options: [] as ActionSheetAction[]
    },
    reportPopupConfig: {
      isShow: false, // 举报弹窗配置
      reportUid: 0
    },
    iosVersion: '1.0.0', // iOS版本
    deviceId: '', // iOS获取的设备ID
    // 记录是否已经提示过
    alreadyReminded: {
      updateToV120: false // 进房后更新轻提示
    }
  }),
  actions: {
    /**
     * 设置是否在IOSWebView打开的页面
     **/
    setIsIOSWebView(value: boolean) {
      this.isIOSWebView = value
    },
    /**
     * 设置iOS版本号
     */
    setIOSVersion(value: string) {
      this.iosVersion = value
    },
    /** 设置设备ID */
    setIOSDeviceID(value: string) {
      this.deviceId = value
    },
    /**
     * 记录是否已提示过
     */
    setAlreadyReminded(key: keyof typeof this.alreadyReminded, value: boolean) {
      this.alreadyReminded[key] = value
    },
    /**
     * 判断当前app版本是否大于等于目标app版本
     * @param version 目标版本号，如"1.2.0"
     * @param isShowToast 是否显示轻提示(当不符合目标版本时) 默认：false
     * @param message 轻提示内容 默认：当前版本不支持此功能，请更新到{v1.2.0}，然后重试
     * @returns {boolean} 是否满足版本要求，即>=目标版本
     */
    checkIOSVersion({
      version,
      isShowToast = false,
      message
    }: {
      version: string
      isShowToast?: boolean
      message?: string
    }): boolean {
      const i18n = useNuxtApp().$i18n
      let isSatisfy = true
      const currentVersion = this.iosVersion.split('.')
      const targetVersion = version.split('.')
      targetVersion.some((ele, index) => {
        if (Number(currentVersion[index] || 0) >= Number(ele)) {
          return false
        }
        isSatisfy = false
        return true
      })
      // 不符合目标版本号，则显示轻提示
      if (!isSatisfy && isShowToast) {
        showToast(message || i18n.t('public.UpdateVersion', [`v${version}`]))
      }
      return isSatisfy
    },

    // 获取iOS版本号
    getIOSVersion(): Promise<string> {
      return new Promise((resolve) => {
        const initialVersion = '1.0.0'
        if (this.iosVersion !== initialVersion) {
          resolve(this.iosVersion)
          return
        }
        if (
          window['webkit']?.messageHandlers?.['getVersionCode'] ||
          window['ReactNativeWebView']?.['getVersionCode']
        ) {
          // 因为旧版本也会进这里，但是旧版本并不会回调
          // 所以加个定时器，如果短时间内没有回调就当做是旧版本
          const TIME = 1000 // 1s
          const timer = setTimeout(() => {
            this.setIOSVersion(initialVersion)
            resolve(initialVersion)
          }, TIME)
          window['appFun']('getVersionCode', (v: string) => {
            // 如果有回调，就清除定时器
            clearTimeout(timer)
            this.setIOSVersion(v)
            resolve(v)
          })
        } else {
          this.setIOSVersion(initialVersion)
          resolve(initialVersion)
        }
      })
    },
    /**
     * 设置keepaLive要缓存的页面
     * @param {1|2} type 1:添加，2：移除
     * @param {string[]} list 要添加的keepaLive页面
     */
    async setKeepAliveIncludePage(type: 1 | 2, list: string[]) {
      if (type === 1) {
        // 去重添加
        this.keepAliveIncludePage = [
          ...new Set([...this.keepAliveIncludePage, ...list])
        ]
      } else if (type === 2) {
        list.forEach(async (v) => {
          const index = this.keepAliveIncludePage.indexOf(v)
          if (index !== -1) {
            const oldList = [...this.keepAliveIncludePage]
            oldList.splice(index, 1)
            this.keepAliveIncludePage = [] // 这样赋值才会生效动态设置
            await nextTick(() => {
              this.keepAliveIncludePage = oldList
            })
          }
        })
      }
    },
    /**
     * 设置页面是否显示
     **/
    setIsPageVisible(value: boolean) {
      this.isPageVisible = value
    },
    /**
     * 引流至App
     * value: 显示或开启
     * content: string:字符串类型: 直接赋值内容
     *          1:Get the full experience on the app
     *          2:Free text messaging on the app
     * trackingLogType: 'msg' | 'call' | 'live'  埋点类型: 消息页，通话后，直播列表页
     */
    setDownloadAppTipsConfig(
      value: boolean,
      content: 1 | 2 | string,
      trackingLogType: 'msg' | 'call' | 'live'
    ) {
      const i18n = useNuxtApp().$i18n
      this.downloadAppTipsPopupConfig.isShow = value
      // 为true再走逻辑
      if (value) {
        this.downloadAppTipsPopupConfig.isLazyShow = true
        this.downloadAppTipsPopupConfig.trackingLogType = trackingLogType
        // 引流至App弹窗显示次数埋点，按细分
        // switch (trackingLogType) {
        //   case 'msg':
        //     eventTracking.eventLog(
        //       eventTracking.eventId.msg_go_to_download_popup
        //     )
        //     break
        //   case 'call':
        //     eventTracking.eventLog(
        //       eventTracking.eventId.call_go_to_download_popup
        //     )
        //     break
        //   case 'live':
        //     eventTracking.eventLog(
        //       eventTracking.eventId.live_go_to_download_popup
        //     )
        //     break
        // }
        // 设置文案
        switch (content) {
          case 1:
            this.downloadAppTipsPopupConfig.tipsText = i18n.t(
              'downloadAppTips.GetTips'
            )
            break
          case 2:
            this.downloadAppTipsPopupConfig.tipsText = i18n.t(
              'downloadAppTips.FreeTips'
            )
            break
          default:
            this.downloadAppTipsPopupConfig.tipsText = content || ''
            break
        }
      }
    },
    /**
     * 设置全局ActionSheet配置
     */
    setActionSheetConfig(config: typeof this.actionSheetConfig) {
      this.actionSheetConfig = config
    },
    /**
     * 打开举报弹窗
     */
    setReportPopupConfig(config: typeof this.reportPopupConfig) {
      this.reportPopupConfig = config
    },
    /**
     * 设置 webViewConfig
     * isShow: 显示或开启
     * src: 打开链接
     * */
    setWebViewPopupConfig(config: {
      isShow: boolean
      src?: string
      popupClass?: string
    }) {
      this.webViewPopupConfig.iShow = config.isShow
      this.webViewPopupConfig.isLazyShow = true
      // 这样关闭是为了保留滑动效果
      if (config.src) {
        this.webViewPopupConfig.src = config.src
      }
      if (config.popupClass || config.popupClass === '') {
        this.webViewPopupConfig.popupClass = config.popupClass
      }
    },
    // 设置是否显示充值弹窗
    setShowRechargePopup(isShow: boolean) {
      this.rechargePopupConfig.isLazyShow = true
      this.rechargePopupConfig.isShow = isShow
    },
    // 设置免费券弹窗显示
    setShowFreeCardPopup(isShow: boolean) {
      this.freeCardPopupConfig.isLazyShow = true
      this.freeCardPopupConfig.isShow = isShow
    },

    /**
     * 是否显示充值提示
     * value: 显示或开启
     * content: string:字符串类型: 直接赋值内容
     *          1:抱歉，你的余额不足，请充值，
     *          2:仅充值一次您就可以开启美好对话！
     *          3:会话上传图片，等级限制提示
     * toType: 'page' as 'page' | 'popup' 点击跳转类型 page:充值页面, popup:充值弹窗，不传则为page
     */
    setRechargeTipsPopup(
      value: boolean,
      content: 1 | 2 | 3 | string,
      toType?: 'page' | 'popup'
    ) {
      const i18n = useNuxtApp().$i18n
      this.rechargeTipsPopupConfig.isShow = value
      this.rechargeTipsPopupConfig.isLazyShow = true
      if (this.rechargeTipsPopupConfig.isShow) {
        this.rechargeTipsPopupConfig.toType = toType || 'page'
        const chatStore = useChatStore()
        switch (content) {
          case 1:
            this.rechargeTipsPopupConfig.tipsText = i18n.t(
              'call.insufficientBalanceTips'
            )
            break
          case 2:
            this.rechargeTipsPopupConfig.tipsText = i18n.t('chat.paidChatMsg')
            break
          case 3:
            this.rechargeTipsPopupConfig.tipsText = i18n.t('chat.sendPicTips', [
              chatStore.chatConfigure.chatLevelLimit
            ])
            break
          default:
            this.rechargeTipsPopupConfig.tipsText = content || ''
            break
        }
      }
    },

    /**
     * 重置登录
     * @param {string} msg 踢出来源，目前做console.log提示
     * @param {string | number} type 踢出类型： init,api,im,socket,socket-kickoff,click
     * @param {boolean} direction 可选，默认true,是否重定向跳转首页
     */
    resetLogin({
      msg,
      type,
      direction = true
    }: {
      msg: string
      type?: string | number
      direction?: boolean
    }) {
      try {
        const i18n = useNuxtApp().$i18n
        const router = useRouter()
        console.log('重置登录函数被触发=>', { msg, type, direction })

        const socketStore = useSocketStore()
        const userStore = useUserStore()
        const chatStore = useChatStore()
        const agoraStore = useAgoraStore()
        const trtcStore = useTrtcStore()
        const callStore = useCallStore()
        const roomStore = useRoomStore()
        const rechargeStore = useRechargeStore()
        const giftStore = useGiftStore()

        // 重置数据
        chatStore.disconnect()
        socketStore.destroy()
        userStore.resetData()
        chatStore.resetData()
        agoraStore.resetData()
        trtcStore.resetData()
        callStore.resetData()
        roomStore.resetData()
        rechargeStore.resetData()
        giftStore.resetData()

        userStore.changeManOnline(2) // 退登前调用,会话打招呼使用

        // 关闭房间和1v1离开页面确认框
        callStore.setIsShowCallRouteLeavDialog(false)
        roomStore.setIsShowLiveRouteLeavDialog(false)

        userStore.userInfo = common.defaultInfo
        localStorage.removeItem('ticket')
        localStorage.removeItem('loginInfo')
        localStorage.removeItem('publicStore')
        this.qnToken = ''

        // gtag 重置设置用户ID
        eventTracking.setUid('')

        // 类型
        switch (type) {
          case 'kicked': // 被踢
            showToast(i18n.t('public.kicked'))
            break
          case 'socket-kickoff':
            showToast(i18n.t('public.kicked'))
            break
          default:
            break
        }

        // FB像素ID移除做特殊处理，init时不移除，暂时不做移除处理
        if (type !== 'init') {
          // localStorage.removeItem('utmFacebookPixelId')
        }

        // 是否重定向首页
        if (direction) {
          router.push('/')
        }
      } catch (error) {
        console.log('退出登录报错==>', error)
      }
    },

    // 设置显示视图
    setIsShowView() {
      this.isShowView = true
    },
    // 七牛云上传token
    setQnToken() {
      getToken().then((res) => {
        const { code, data } = res
        if (code === 200) {
          this.qnToken = data
        }
      })
    },
    // 国家列表
    async setCountryList() {
      if (this.countryList.length) {
        return
      }
      await apiGetCountryList().then(
        (res: { code: number; data: countryInterface[] }) => {
          const { code, data } = res
          if (code === 200) {
            this.countryList = data
          }
        }
      )
    },
    // 获取大洲列表
    async getContinentCountryList() {
      const languageStore = useLanguageStore()
      // 做语言判断，语言和之前的不一致，就更新数据
      if (
        this.continentCountryList.length &&
        languageStore.languageInfo.value === this.countryLanguage
      )
        return
      const res = await apiGetContinentCountryList()
      this.countryLanguage = languageStore.languageInfo.value
      const { code, data } = res
      if (code === 200) {
        // data.forEach((val) => {
        //   val.countryList = val.countryList.filter((v) => {
        //     return v.countryId
        //   })
        // })
        this.continentCountryList = data
      }
    },
    /**
     * 获取客户端配置
     */
    async getClientConfigure() {
      const res = await apiGetClientConfigure()
      const { code, data } = res
      if (code === 200) {
        this.clientConfigure = data
      }
    }
  },
  persist: {
    key: 'publicStore',
    paths: [
      'qnToken',
      'countryList',
      'continentCountryList',
      'countryLanguage',
      'alreadyReminded'
    ]
  }
})
